import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import AnalysisFormatter, { formatTextForPdf } from '../components/analysis/AnalysisFormatter';
import { deleteAnalysis, cleanupInvalidAnalyses } from '../services/analysis';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const HistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: var(--background-gradient);
  color: var(--text-color);
  font-family: 'Poppins', sans-serif;
  position: relative;
  overflow-x: hidden;
`;

const HistoryPageContainer = styled.div`
  padding: 30px;
  max-width: 1000px;
  margin: 40px auto;
  background-color: rgba(10, 0, 30, 0.35);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(255, 64, 192, 0.1);
  position: relative;
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 20px 10px;
    width: calc(100% - 20px);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 64, 192, 0.1) 0%, rgba(255, 208, 0, 0.05) 100%);
    border-radius: 20px;
    pointer-events: none;
    z-index: -1;
  }
`;

const Title = styled.h1`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 2.2rem;
  margin-bottom: 30px;
  color: var(--primary-color);
  text-align: center;
  text-shadow: 0 2px 10px rgba(255, 64, 192, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
  
  &::after {
    content: "";
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    margin: 15px auto 0;
    border-radius: 3px;
  }
`;

const NoHistoryMessage = styled.div`
  text-align: center;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  padding: 60px 30px;
  background-color: rgba(10, 0, 30, 0.25);
  border-radius: 20px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 64, 192, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(255, 64, 192, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 20px;
  color: var(--primary-color);
  opacity: 0.8;
  position: relative;
  
  svg {
    filter: drop-shadow(0 4px 10px rgba(255, 64, 192, 0.4));
  }
  
  &::after {
    content: "";
    position: absolute;
    width: 60px;
    height: 60px;
    background: radial-gradient(circle, rgba(255, 64, 192, 0.2) 0%, transparent 70%);
    border-radius: 50%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
    animation: pulse 2s infinite ease-in-out;
  }
  
  @keyframes pulse {
    0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
    50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
    100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
  }
`;

const EmptyStateText = styled.div`
  margin-bottom: 15px;
  font-weight: 500;
  color: white;
`;

const EmptyStateSubtext = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 300px;
  margin: 0 auto;
  line-height: 1.5;
`;

const HistoryList = styled.ul`
  list-style: none;
  padding: 0;
`;

const HistoryItem = styled.li`
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.3),
      0 0 15px rgba(255, 64, 192, 0.15);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
    display: grid;
    grid-template-columns: auto auto;
    gap: 8px;
    
    /* Metti il pulsante Delete sulla stessa riga ma a destra */
    & > *:last-child {
      grid-column: 2;
      justify-self: flex-end;
    }
    
    /* Metti il costo e il pulsante Download sulla prima riga */
    & > *:first-child {
      grid-column: 1;
    }
    
    & > *:nth-child(2) {
      grid-column: 2;
      justify-self: flex-end;
    }
  }
`;

const Timestamp = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  
  i {
    margin-right: 8px;
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Cost = styled.span`
  font-size: 0.9rem;
  color: var(--secondary-color);
  font-weight: 500;
  background-color: rgba(255, 208, 0, 0.1);
  padding: 5px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  
  i {
    margin-right: 6px;
    font-size: 0.8rem;
  }
`;

const DownloadButton = styled.button`
  background-color: var(--secondary-color);
  color: #170032;
  border: none;
  border-radius: 30px;
  padding: 5px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin: 20px auto 0;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    justify-content: center;
    width: auto;
    min-width: 44px;
  }
  
  @media (max-width: 360px) {
    .button-text {
      display: none; /* Nascondi il testo su schermi molto piccoli */
    }
    
    padding: 8px;
    gap: 0;
  }
  
  &:hover {
    background-color: #FFE03D;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 208, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    font-size: 0.9rem;
  }
`;

const DeleteButton = styled.button`
  background-color: rgba(255, 64, 96, 0.2);
  color: #ff4060;
  border: none;
  border-radius: 30px;
  padding: 5px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    justify-content: center;
    width: auto;
    min-width: 44px;
  }
  
  @media (max-width: 360px) {
    .button-text {
      display: none; /* Nascondi il testo su schermi molto piccoli */
    }
    
    padding: 8px;
    gap: 0;
  }
  
  &:hover {
    background-color: rgba(255, 64, 96, 0.35);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    font-size: 0.9rem;
  }
`;

const DetailsSummary = styled.div`
  margin-bottom: 15px;
  background-color: rgba(0, 0, 0, 0.15);
  padding: 15px;
  border-radius: 10px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const DetailLabel = styled.strong`
  color: var(--primary-color);
  margin-right: 5px;
`;

const DetailsContent = styled.div`
  margin-bottom: 15px;
  background-color: rgba(0, 0, 0, 0.15);
  padding: 20px;
  border-radius: 10px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 64, 192, 0.05) 0%, rgba(255, 208, 0, 0.03) 100%);
    pointer-events: none;
    z-index: 0;
    border-radius: 10px;
  }
`;

const ResultTitle = styled.div`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 1.4rem;
  color: var(--primary-color);
  margin-bottom: 15px;
  text-shadow: 0 2px 10px rgba(255, 64, 192, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  i {
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-top: 20px;
  padding: 12px 24px;
  background: linear-gradient(45deg, var(--primary-color) 0%, #ff65d5 100%);
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 64, 192, 0.3);
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    width: 100%;
    justify-content: center;
  }
  
  i {
    margin-right: 8px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 64, 192, 0.5);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 25px 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const PaginationButton = styled.button`
  padding: 8px 15px;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.3)'};
  color: white;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    min-width: 44px;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 64, 192, 0.3)'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(255,64,96,0.1);
  color: #ff4060; 
  padding: 10px; 
  border-radius: 10px; 
  margin-bottom: 20px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background-color: rgba(0, 200, 83, 0.1);
  color: #00C853; 
  padding: 10px; 
  border-radius: 10px; 
  margin-bottom: 20px;
  text-align: center;
`;

// PDF styles for dark mode (hidden container)
const PdfContainer = styled.div`
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 794px; // A4 width in pixels at 96 DPI
  background-color: #170032;
  padding: 40px;
  font-family: 'Poppins', sans-serif;
  color: white;
  
  .pdf-section-title {
    color: var(--secondary-color);
    font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
    font-size: 20px;
    font-weight: 600;
    margin: 20px 0 12px;
    border-bottom: 1px solid rgba(255, 208, 0, 0.3);
    padding-bottom: 6px;
  }
  
  .pdf-subsection-title {
    color: var(--primary-color);
    font-size: 18px;
    font-weight: 600;
    margin: 16px 0 8px;
  }
  
  .pdf-highlight {
    color: var(--accent-color);
    font-weight: 600;
  }
  
  .pdf-text {
    margin-bottom: 12px;
    font-size: 15px;
    line-height: 1.6;
  }
  
  .pdf-header {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .pdf-title {
    color: var(--primary-color);
    font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: 0.5px;
  }
  
  .pdf-date {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 25px;
  }
`;

const AnalysisHistoryPage = () => {
  const { currentUser, updateUserData } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState('');
  const [filteredHistory, setFilteredHistory] = useState([]);
  const itemsPerPage = 5;

  // Effettua la pulizia automatica delle analisi invalide al caricamento della pagina
  useEffect(() => {
    if (currentUser && currentUser.analysis_history) {
      console.log('[AnalysisHistoryPage] currentUser.analysis_history (raw):', JSON.parse(JSON.stringify(currentUser.analysis_history)));
      if (currentUser.analysis_history.length > 0) {
        const validAnalyses = filterValidAnalyses(currentUser.analysis_history);
        console.log('[AnalysisHistoryPage] filteredHistory after filterValidAnalyses:', JSON.parse(JSON.stringify(validAnalyses)));
        setFilteredHistory(validAnalyses);
        handleAutomaticCleanup();
      } else {
        setFilteredHistory([]);
      }
    } else {
      console.log('[AnalysisHistoryPage] currentUser or currentUser.analysis_history is null/undefined');
      setFilteredHistory([]);
    }
  }, [currentUser]);

  // Funzione per filtrare localmente le analisi valide
  const filterValidAnalyses = (analyses) => {
    return analyses.filter(analysis => {
      // Verifica se il testo dell'analisi esiste ed è valido
      const analysisText = analysis.result ? analysis.result.trim() : '';
      
      // Controllo lunghezza minima
      if (analysisText.length < 50) {
        return false;
      }
      
      // Controllo per placeholder o testo non valido
      const invalidi = ["N/A", "Date not available", "undefined", "error", "null"];
      if (invalidi.some(term => analysisText.toLowerCase().includes(term.toLowerCase()))) {
        return false;
      }
      
      // Controllo immagini associate
      const imageSummary = analysis.image_summary || {};
      if (!imageSummary.count || imageSummary.count === 0 || !imageSummary.filenames || imageSummary.filenames.length === 0) {
        return false;
      }
      
      return true;
    });
  };

  // Funzione per eseguire la pulizia automatica delle analisi invalide
  const handleAutomaticCleanup = async () => {
    try {
      setIsCleaning(true);
      
      // Esegue la pulizia tramite l'API
      const data = await cleanupInvalidAnalyses();
      console.log("Automatic cleanup results:", data);
      
      if (data.removed_count > 0) {
        console.log(`Automatically removed ${data.removed_count} invalid analyses`);
        
        // Aggiorna i dati dell'utente con quelli puliti
        const token = localStorage.getItem('token');
        const updatedUser = await fetch('https://api.instarizz.me/api/auth/me', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).then(r => r.json());
        
        updateUserData(updatedUser);
      }
    } catch (error) {
      console.error("Error in automatic cleanup:", error);
    } finally {
      setIsCleaning(false);
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!currentUser || isDeleting) return;
    
    // Conferma prima di eliminare
    if (window.confirm("Are you sure you want to delete this analysis? This action is irreversible.")) {
      try {
        setIsDeleting(true);
        setDeleteError(null);
        
        // Chiama il servizio per eliminare l'analisi
        await deleteAnalysis(analysisId);
        
        // Aggiorna lo stato locale
        const updatedHistory = currentUser.analysis_history.filter(
          item => item.analysis_id !== analysisId
        );
        
        // Aggiorna lo stato dell'utente nel contesto
        updateUserData({
          ...currentUser,
          analysis_history: updatedHistory
        });
        
      } catch (error) {
        console.error("Error deleting analysis:", error);
        setDeleteError("Unable to delete the analysis. Please try again later.");
        
        // Aggiorna comunque l'interfaccia utente per una migliore UX
        // anche se l'operazione sul server è fallita
        const updatedHistory = currentUser.analysis_history.filter(
          item => item.analysis_id !== analysisId
        );
        
        updateUserData({
          ...currentUser,
          analysis_history: updatedHistory
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (!currentUser || !filteredHistory || filteredHistory.length === 0) {
    return (
      <HistoryContainer>
        <HistoryPageContainer>
          <Title>Analysis History</Title>
          <NoHistoryMessage>
            <EmptyStateIcon>
              <i className="fas fa-search"></i>
            </EmptyStateIcon>
            <EmptyStateText>You have no past analyses yet.</EmptyStateText>
            <EmptyStateSubtext>
              Start by analyzing Instagram profiles from the dashboard to see your history here.
            </EmptyStateSubtext>
          </NoHistoryMessage>
          <BackLink to="/dashboard">
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </BackLink>
        </HistoryPageContainer>
      </HistoryContainer>
    );
  }

  // Ordina lo storico dal più recente al meno recente
  const sortedHistory = [...filteredHistory].sort((a, b) => {
    // Funzione ausiliaria per ottenere un timestamp numerico valido anche se la data è invalida
    const getTimestamp = (item) => {
      try {
        // Prima controlla il campo timestamp
        if (item.timestamp) {
          const date = new Date(item.timestamp);
          if (!isNaN(date.getTime())) {
            return date.getTime();
          }
        }
        
        // Prova con created_at se timestamp non è valido
        if (item.created_at) {
          const date = new Date(item.created_at);
          if (!isNaN(date.getTime())) {
            return date.getTime();
          }
        }
        
        // Fallback per date non valide: usa l'ID come ordinamento secondario
        // L'assunzione è che gli ID più recenti siano generalmente più grandi
        return item.id ? item.id.toString() : '0';
      } catch (e) {
        console.error("Error processing date:", e);
        return 0; // Fallback estremo
      }
    };
    
    // Ottieni i timestamp come numeri e confrontali
    const timeA = getTimestamp(b); // Nota: b è prima di a per l'ordinamento discendente
    const timeB = getTimestamp(a);
    
    // Se entrambi sono stringhe, usa il confronto lessicografico inverso
    if (typeof timeA === 'string' && typeof timeB === 'string') {
      return timeA.localeCompare(timeB);
    }
    
    // Altrimenti usa la sottrazione standard per comparare i numeri
    return timeA - timeB;
  });
  
  // Calcolo della paginazione (ripristinato qui)
  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage);
  const currentItems = sortedHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <HistoryContainer>
      <HistoryPageContainer>
        <Title>Your Analysis History</Title>
        
        {deleteError && (
          <ErrorMessage>{deleteError}</ErrorMessage>
        )}
        
        <HistoryList>
          {currentItems.map((item) => (
            <HistoryItem key={item.analysis_id}>
              <ItemHeader>
                <Timestamp>
                  <i className="far fa-calendar-alt"></i>
                  {(() => {
                    try {
                      // Prova prima con il timestamp
                      if (item.timestamp) {
                        const date = new Date(item.timestamp);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleString();
                        }
                      }
                      
                      // Prova poi con created_at
                      if (item.created_at) {
                        const date = new Date(item.created_at);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleString();
                        }
                      }
                      
                      // Fallback se entrambe le date sono invalide
                      return "Date not available";
                    } catch (e) {
                      console.error("Error displaying date:", e);
                      return "Date not available";
                    }
                  })()}
                </Timestamp>
                <HeaderActions>
                  <Cost>
                    <i className="fas fa-coins"></i>
                    {item.cost || 1} Credit{(item.cost || 1) !== 1 ? 's' : ''}
                  </Cost>
                  <DeleteButton 
                    onClick={() => handleDeleteAnalysis(item.analysis_id)}
                    // disabled={isDeleting} // Rimuovi se isDeleting non ha altri usi o gestiscilo
                  >
                    <i className="fas fa-trash-alt"></i>
                    <span className="button-text">Delete</span>
                  </DeleteButton>
                </HeaderActions>
              </ItemHeader>
              <DetailsSummary>
                <DetailLabel>Context:</DetailLabel>
                <span>{item.parameters?.context || 'General'}</span><br />
                <DetailLabel>Specific Goal:</DetailLabel>
                <span>{item.parameters?.specific_goal || 'None'}</span><br />
                <DetailLabel>Images Analyzed:</DetailLabel>
                <span>{item.image_summary?.count || 'N/A'}</span>
                {item.image_summary?.filenames && item.image_summary.filenames.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginLeft: '10px', marginTop: '5px' }}>
                    <i className="fas fa-image" style={{ marginRight: '5px' }}></i>
                    {item.image_summary.filenames.join(', ')}
                  </div>
                )}
              </DetailsSummary>
              <DetailsContent>
                <ResultTitle>
                  <i className="fas fa-chart-line"></i>
                  Result:
                </ResultTitle>
                <AnalysisFormatter analysisText={item.result} />
              </DetailsContent>
            </HistoryItem>
          ))}
        </HistoryList>
        
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left"></i>
            </PaginationButton>
            
            {[...Array(totalPages)].map((_, i) => (
              <PaginationButton
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </PaginationButton>
            ))}
            
            <PaginationButton 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-chevron-right"></i>
            </PaginationButton>
          </PaginationContainer>
        )}
        
        <BackLink to="/dashboard">
          <i className="fas fa-arrow-left"></i>
          Back to Dashboard
        </BackLink>
      </HistoryPageContainer>
    </HistoryContainer>
  );
};

export default AnalysisHistoryPage; 